import { useId, useMemo } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CHART_ANIMATION_DURATION_MS,
  CHART_ANIMATION_EASING,
  chartMountKey,
  seriesAnimationBegin,
} from "./chartAnimation";
import { NbsChartLegend } from "./NbsChartLegend";

export type NbsAreaChartProps = {
  readonly data: readonly Record<string, string | number>[];
  readonly xKey: string;
  readonly seriesKeys: readonly string[];
  readonly height?: number;
  readonly colors?: readonly string[];
  /** Superposé (overlay) by default — never imply intermediate years. */
  readonly stacked?: boolean;
  readonly unit?: string | null;
};

const DEFAULT_COLORS = [
  "#000091",
  "#465fff",
  "#009081",
  "#6a6af4",
  "#e4794a",
  "#0063cb",
  "#18753c",
  "#b34000",
];

function safeDomId(raw: string): string {
  return raw.replace(/[^a-zA-Z0-9_-]/g, "");
}

export function NbsAreaChart({
  data,
  xKey,
  seriesKeys,
  height = 340,
  colors = DEFAULT_COLORS,
  stacked = false,
  unit,
}: NbsAreaChartProps) {
  const reactId = safeDomId(useId());
  const chartData = useMemo(() => [...data], [data]);
  const mountKey = chartMountKey(chartData, xKey, seriesKeys, stacked ? "s" : "o");

  return (
    <div data-testid="nbs-area-chart" style={{ width: "100%" }}>
      <div style={{ width: "100%", height }}>
        <ResponsiveContainer width="100%" height="100%" debounce={50}>
          <AreaChart
            key={mountKey}
            data={chartData}
            margin={{ top: 12, right: 16, left: 4, bottom: 8 }}
          >
            <defs>
              {seriesKeys.map((key, i) => {
                const color = colors[i % colors.length]!;
                const id = `nbs-area-grad-${reactId}-${i}`;
                const topOpacity = stacked ? 0.72 : 0.42;
                return (
                  <linearGradient key={key} id={id} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color} stopOpacity={topOpacity} />
                    <stop offset="95%" stopColor={color} stopOpacity={0.02} />
                  </linearGradient>
                );
              })}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" vertical={false} />
            <XAxis
              dataKey={xKey}
              type="category"
              tick={{ fontSize: 12, fill: "#5c6b63" }}
              tickLine={false}
              axisLine={{ stroke: "rgba(0,0,0,0.12)" }}
              interval={0}
            />
            <YAxis
              tick={{ fontSize: 12, fill: "#5c6b63" }}
              tickLine={false}
              axisLine={false}
              {...(unit
                ? {
                    label: {
                      value: unit,
                      angle: -90,
                      position: "insideLeft" as const,
                      style: { fontSize: 11 },
                    },
                  }
                : {})}
            />
            <Tooltip
              formatter={(value: number, name: string) => [unit ? `${value} ${unit}` : value, name]}
              contentStyle={{
                borderRadius: 10,
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
              }}
            />
            {seriesKeys.map((key, i) => {
              const color = colors[i % colors.length]!;
              const gradId = `nbs-area-grad-${reactId}-${i}`;
              return (
                <Area
                  key={`${mountKey}-${key}`}
                  type="monotone"
                  dataKey={key}
                  name={key}
                  stroke={color}
                  fill={`url(#${gradId})`}
                  strokeWidth={2}
                  {...(stacked ? { stackId: "landcover" } : {})}
                  connectNulls={false}
                  isAnimationActive
                  animationBegin={seriesAnimationBegin(i)}
                  animationDuration={CHART_ANIMATION_DURATION_MS}
                  animationEasing={CHART_ANIMATION_EASING}
                  dot={{ r: 3, strokeWidth: 1, stroke: "#fff", fill: color }}
                  activeDot={{ r: 6, strokeWidth: 2, stroke: "#fff" }}
                />
              );
            })}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <NbsChartLegend seriesKeys={seriesKeys} colors={colors} />
    </div>
  );
}
